import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { CsvService } from "../../services/csvService";
import { PageHeader } from "../components/ApplicationShell";
import Alert from "../components/Alert";
import Button from "../components/Button";
import Dialog from "../components/Dialog";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import StatusBadge from "../components/StatusBadge";

const getId = (item) => item?.id || item?._id;
const formatDate = (value) => value && moment(value).isValid() ? moment(value).format("D MMM YYYY, HH:mm") : "Not specified";
const statusType = (status) => status === "completed" ? "success" : status === "failed" ? "error" : "warning";
const getError = (error, fallback = "Data exports could not be loaded.") => error?.response?.data?.message
  || error?.data?.message || error?.message || error?.error || fallback;

const DataExports = () => {
  const [exports, setExports] = useState([]);
  const [pageInfo, setPageInfo] = useState({ last_page: 1, total: 0 });
  const [query, setQuery] = useState({ page: 0, pageSize: 10, sortBy: "started_at", sortOrder: "desc" });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadExports = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const response = await CsvService.getCsvs(query.page, query.pageSize, query.sortBy, query.sortOrder, query.filters || {});
      const records = response?.data || [];
      setExports(records);
      setPageInfo({ last_page: Number(response?.last_page) || 1, total: Number(response?.total) || records.length });
    } catch (requestError) { setError(getError(requestError)); }
    finally { setLoading(false); }
  }, [query]);

  useEffect(() => { loadExports(); }, [loadExports]);

  const columns = useMemo(() => [
    { key: "started_at", header: "Started", accessor: (item) => formatDate(item.started_at || item.created_at), sortValue: (item) => item.started_at || item.created_at },
    { key: "format", header: "Format", accessor: (item) => String(item.format || "CSV").toUpperCase(), filter: { type: "select", options: [{ label: "CSV", value: "CSV" }, { label: "Excel", value: "XLSX" }] } },
    { key: "status", header: "Status", accessor: (item) => item.status || "pending", render: (item) => <div><StatusBadge type={statusType(item.status)}>{item.status === "completed" ? "Ready" : item.status === "failed" ? "Failed" : "Processing"}</StatusBadge>{item.status === "failed" && item.failure_reason && <div><small>{item.failure_reason}</small></div>}</div>, sortable: false },
    { key: "filename", header: "File", accessor: (item) => item.filename ? item.filename.split(/[\\/]/).pop() : "Generated when ready", sortable: false },
  ], []);

  const downloadExport = useCallback(async (item) => {
    setDownloadError("");
    try {
      await CsvService.downloadCsv(getId(item), item.format, item.filename);
    } catch (requestError) {
      const filename = item.filename?.split(/[\\/]/).pop() || "The export";
      setDownloadError(getError(requestError, `${filename} could not be downloaded. Check your connection and try again.`));
    }
  }, []);

  const actions = useMemo(() => [
    { label: "Download", loadingLabel: "Downloading...", isVisible: (item) => item.status === "completed" && Boolean(getId(item)), onClick: downloadExport },
    { label: "Delete export", onClick: setDeleteTarget },
  ], [downloadExport]);

  const removeExport = async () => {
    if (!deleteTarget) return;
    setWorking(true); setError("");
    try { await CsvService.deleteCsv(getId(deleteTarget)); setDeleteTarget(null); await loadExports(); }
    catch (requestError) { setError(getError(requestError)); }
    finally { setWorking(false); }
  };

  const handleQuery = useCallback(({ filters, sortBy }) => {
    const sort = sortBy?.[0];
    setQuery((current) => {
      const next = { ...current, filters, page: 0, sortBy: sort?.id || "started_at", sortOrder: sort?.desc ? "desc" : "asc" };
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, []);

  return (<>
    <PageHeader description="Download completed pack-register exports or remove files you no longer need." title="Data exports" />
    {downloadError && <Alert title="Download failed" type="error">{downloadError}</Alert>}
    <ResponsiveDataTable actions={actions} caption="Data exports" columns={columns} data={exports} emptyDescription="Exports created from the packs register will appear here."
      emptyTitle="No data exports" error={error} initialSort={{ key: "started_at", direction: "desc" }} loading={loading || working} manual
      onPageChange={(page) => setQuery((current) => ({ ...current, page }))} onPageSizeChange={(pageSize) => setQuery((current) => ({ ...current, page: 0, pageSize }))}
      onQueryChange={handleQuery} onRetry={loadExports} pageCount={Math.max(pageInfo.last_page, 1)} pageIndex={query.page} pageSize={query.pageSize}
      showColumnSelector={false} showCount={false} showExport={false} showSearch={false} title="Exports" totalRows={pageInfo.total} />
    <Dialog actions={<><Button disabled={working} onClick={() => setDeleteTarget(null)} variant="outline">Cancel</Button><Button loading={working} onClick={removeExport} variant="destructive">Delete export</Button></>}
      description="The generated file will be permanently removed." onClose={() => !working && setDeleteTarget(null)} open={Boolean(deleteTarget)} size="small" title="Delete this data export?">
      <p>Delete <strong>{deleteTarget?.filename?.split(/[\\/]/).pop() || "this export"}</strong>?</p>
    </Dialog>
  </>);
};

export default DataExports;
